// src/pages/BeforeAfterCreator.js
import React, { useState, useRef, useEffect } from 'react';
import { Camera, Image as LucideImage, Loader, SwitchCamera } from 'lucide-react';
import { createCompositeImage } from '../data/mockData';
import { storageManager } from '../utils/storageManager'; 
import { useTracker } from '../hooks/useAnalytics';

// Before/After Creator Component
const BeforeAfterCreator = ({ photo, navigateTo, setUserPhotos,addMemoryAndSave  }) => {
  const [capturedPhoto, setCapturedPhoto] = useState(null);
  const [isCameraActive, setIsCameraActive] = useState(false);
  const [isCreatingMemory, setIsCreatingMemory] = useState(false);
  const [compositeProgress, setCompositeProgress] = useState(0);
  const [facingMode, setFacingMode] = useState('environment'); // 'environment' = posteriore, 'user' = anteriore
  const [currentStream, setCurrentStream] = useState(null);
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const { trackBeforeAfter, trackCustom } = useTracker();

  // Start camera when component mounts if camera is active
  useEffect(() => {
    if (isCameraActive) {
      startCamera();
    }
    
    return () => {
      stopCamera();
    };
  }, [isCameraActive, facingMode]); // Aggiungi facingMode nelle dipendenze
  
  // Start camera function
  const startCamera = async () => {
    try {
      // Ferma il stream precedente se esiste
      if (currentStream) {
        currentStream.getTracks().forEach(track => track.stop());
        setCurrentStream(null);
      }

      const stream = await navigator.mediaDevices.getUserMedia({ 
        video: { 
          facingMode: facingMode,
          width: { ideal: 1920 },
          height: { ideal: 1080 }
        } 
      });
      
      setCurrentStream(stream);
      
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
      }
    } catch (err) {
      console.error('Error accessing camera:', err);
      alert('Non è stato possibile accedere alla fotocamera. Controlla le autorizzazioni.');
      setIsCameraActive(false);
    }
  };
  
  
  // Stop camera function
  const stopCamera = () => {
    if (currentStream) {
      currentStream.getTracks().forEach(track => track.stop());
      setCurrentStream(null);
    }
    if (videoRef.current && videoRef.current.srcObject) {
      videoRef.current.srcObject = null;
    }
  };
  const switchCamera = () => {
    setFacingMode(prevMode => prevMode === 'environment' ? 'user' : 'environment');
  };
  
  
  // Capture photo from camera
  const capturePhoto = () => {
    if (videoRef.current && canvasRef.current) {
      trackCustom('before_after_photo_captured', {
        historicalYear: photo.year,
        cameraMode: facingMode
      });
      const video = videoRef.current;
      const canvas = canvasRef.current;
      
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      
      const ctx = canvas.getContext('2d');
      ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
      
      // Get captured image data
      const imageDataUrl = canvas.toDataURL('image/jpeg', 0.8);
      setCapturedPhoto(imageDataUrl);
      setIsCameraActive(false);
      stopCamera();
    }
  };
  
  // Convert data URL to Blob
  const dataURLToBlob = (dataURL) => {
    const arr = dataURL.split(',');
    const mime = arr[0].match(/:(.*?);/)[1];
    const bstr = atob(arr[1]);
    let n = bstr.length;
    const u8arr = new Uint8Array(n);
    
    while(n--){
      u8arr[n] = bstr.charCodeAt(n);
    }
    
    return new Blob([u8arr], { type: mime });
  };
  
  // Create memory using backend API
  const createMemory = async () => {
    if (!capturedPhoto || !photo) {
      alert('Assicurati di aver scattato una foto prima di creare il ricordo.');
      return;
    }
    
    setIsCreatingMemory(true);
    setCompositeProgress(0);
    
    try {
      console.log('Creating memory with backend API...');
      
      const baId = await trackBeforeAfter(photo.year, new Date().getFullYear());

      // Simulate progress updates
      const progressInterval = setInterval(() => {
        setCompositeProgress(prev => {
          if (prev < 90) return prev + 10;
          return prev;
        });
      }, 200);
      
      // Convert captured photo to blob
      const userPhotoBlob = dataURLToBlob(capturedPhoto);
      
      // Create composite image using backend API
      const compositeResult = await createCompositeImage(photo, userPhotoBlob);
      
      clearInterval(progressInterval);
      setCompositeProgress(100);
      
      // Create memory object
      const newMemory = {
        id: Date.now(),
        baId: baId, 
        historicalPhoto: photo,
        userPhoto: capturedPhoto,
        createdAt: new Date().toISOString(),
        compositeImage: compositeResult.compositeUrl,
        ...compositeResult
      };
      
      console.log('Memory created successfully:', newMemory);
      
      // 🔥 NUOVO: Usa la funzione per salvare nel storage
      const saved = await addMemoryAndSave(newMemory);
      if (saved) {
        console.log('✅ Ricordo salvato con successo nel storage locale');
      } else {
        console.warn('⚠️ Ricordo creato ma non salvato permanentemente');
      }
      
      // Navigate to memories page with a slight delay to show completion
      setTimeout(() => {
        setIsCreatingMemory(false);
        navigateTo('myMemories');
      }, 1000);
      
    } catch (error) {
      console.error('Error creating memory:', error);
      setIsCreatingMemory(false);
      setCompositeProgress(0);
      alert('Si è verificato un errore durante la creazione del ricordo. Riprova.');
    }
  };
  // Render camera view
  if (isCameraActive) {
    return (
      <div className="container mx-auto px-4 py-6 text-amber-100">
        <h2 className="text-xl font-bold mb-4 text-center text-amber-300">Scatta la tua foto</h2>
        
        <div className="relative mb-4">
          <div className="bg-black rounded-lg overflow-hidden">
            <video 
              ref={videoRef} 
              autoPlay 
              playsInline 
              className="w-full h-auto" 
            />
          </div>
          
          {/* Overlay della foto storica per guidare la composizione */}
          <div className="absolute inset-0 pointer-events-none opacity-20">
            <img 
              src={photo.fullSize} 
              alt={photo.title} 
              className="w-full h-full object-contain" 
            />
          </div>
          
          {/* Frame di guida */}
          <div className="absolute inset-4 border-2 border-amber-500 rounded-lg pointer-events-none opacity-70"></div>
        </div>
        
        <div className="fixed bottom-10 left-1/2 transform -translate-x-1/2 w-full max-w-md px-4 pb-10">
          <p className="text-sm mb-4 text-center text-amber-200">
            Posizionati per replicare l'angolazione della foto originale
          </p>
          
          <div className="flex justify-center gap-4">
            <button 
              onClick={() => {
                stopCamera(); // Usa stopCamera invece di solo setIsCameraActive(false)
                setIsCameraActive(false);
              }}
              className="py-3 px-6 bg-stone-700 hover:bg-stone-600 text-amber-100 rounded-lg transition-colors"
            >
              Annulla
            </button>
            
            <button 
              onClick={switchCamera}
              className="w-16 h-16 rounded-full bg-blue-600 hover:bg-blue-700 flex items-center justify-center border-4 border-white shadow-lg transition-colors"
              title={facingMode === 'environment' ? 'Passa alla fotocamera anteriore' : 'Passa alla fotocamera posteriore'}
            >
              <SwitchCamera size={24} color="white" />
            </button>
            
            <button 
              onClick={capturePhoto}
              className="w-16 h-16 rounded-full bg-red-600 hover:bg-red-700 flex items-center justify-center border-4 border-white shadow-lg transition-colors"
            >
              <Camera size={24} color="white" />
            </button>
          </div>
        </div>
        
        <canvas ref={canvasRef} className="hidden" />
      </div>
    );
  }
  
  // Render creation progress
  if (isCreatingMemory) {
    return (
      <div className="container mx-auto px-4 py-6 text-amber-100">
        <div className="max-w-md mx-auto bg-stone-800 rounded-lg p-6 text-center">
          <Loader className="w-16 h-16 mx-auto mb-4 text-amber-500 animate-spin" />
          <h2 className="text-xl font-bold mb-4 text-amber-300">Creando il tuo ricordo...</h2>
          
          <div className="mb-4">
            <div className="bg-stone-700 rounded-full h-4 overflow-hidden">
              <div 
                className="bg-gradient-to-r from-amber-500 to-amber-400 h-full transition-all duration-300 ease-out"
                style={{ width: `${compositeProgress}%` }}
              ></div>
            </div>
            <p className="text-sm mt-2 text-amber-200">{compositeProgress}% completato</p>
          </div>
          
          <div className="text-sm text-amber-200">
            {compositeProgress < 30 && "Caricamento delle immagini..."}
            {compositeProgress >= 30 && compositeProgress < 60 && "Elaborazione in corso..."}
            {compositeProgress >= 60 && compositeProgress < 90 && "Creazione del composito..."}
            {compositeProgress >= 90 && compositeProgress < 100 && "Finalizzazione..."}
            {compositeProgress === 100 && "Ricordo creato con successo!"}
          </div>
        </div>
      </div>
    );
  }
  
  return (
    <div className="container mx-auto px-4 py-6 text-amber-100">
      <h2 className="text-xl font-bold mb-4 text-center text-amber-300">Crea il tuo ricordo</h2>
      
      <div className="grid grid-cols-2 gap-4 mb-6">
        <div className="bg-stone-800 rounded-lg overflow-hidden shadow">
          <div className="p-2 bg-stone-700 text-center text-sm text-amber-300 font-medium">
            Prima ({photo.year})
          </div>
          <img 
            src={photo.fullSize} 
            alt={photo.title} 
            className="w-full aspect-square object-cover" 
          />
          <div className="p-2 text-xs text-amber-200 text-center">
            {photo.title}
          </div>
        </div>
        
        <div className="bg-stone-800 rounded-lg overflow-hidden shadow">
          <div className="p-2 bg-stone-700 text-center text-sm text-amber-300 font-medium">
            Adesso (2025)
          </div>
          {capturedPhoto ? (
            <>
              <img 
                src={capturedPhoto} 
                alt="Foto attuale" 
                className="w-full aspect-square object-cover" 
              />
              <div className="p-2 text-xs text-amber-200 text-center">
                Foto scattata
              </div>
            </>
          ) : (
            <div 
              className="w-full aspect-square bg-stone-700 flex items-center justify-center cursor-pointer hover:bg-stone-600 transition-colors"
              onClick={() => setIsCameraActive(true)}
            >
              <div className="text-center">
                <Camera size={32} className="mx-auto mb-2" />
                <span>Scatta foto</span>
              </div>
            </div>
          )}
        </div>
      </div>
      
      {capturedPhoto ? (
        <div className="space-y-4">
          <button 
            onClick={() => {
              setCapturedPhoto(null);
              setCompositeProgress(0);
            }}
            className="w-full py-3 bg-stone-700 hover:bg-stone-600 text-amber-100 rounded-lg shadow transition-colors flex items-center justify-center"
          >
            <Camera size={20} className="mr-2" />
            Scatta di nuovo
          </button>
          
          <button 
            onClick={createMemory}
            className="w-full py-3 bg-green-700 hover:bg-green-600 text-white rounded-lg shadow transition-colors flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed"
            disabled={isCreatingMemory}
          >
            {isCreatingMemory ? (
              <Loader size={20} className="mr-2 animate-spin" />
            ) : (
              <LucideImage size={20} className="mr-2" />
            )}
            {isCreatingMemory ? 'Creazione in corso...' : 'Crea ricordo'}
          </button>
          
          <div className="text-xs text-amber-200 text-center">
            Il tuo ricordo verrà creato combinando la foto storica con la tua foto attuale.
          </div>
        </div>
      ) : (
        <button 
          onClick={() => setIsCameraActive(true)}
          className="w-full py-3 bg-green-700 hover:bg-green-600 text-white rounded-lg shadow transition-colors flex items-center justify-center"
        >
          <Camera size={20} className="mr-2" />
          Inizia a scattare
        </button>
      )}
      
      <canvas ref={canvasRef} className="hidden" />
    </div>
  );
};

export default BeforeAfterCreator;